import React, { useState } from 'react';
import { X } from 'lucide-react';
import { FindingType, FindingSeverity, FindingSourceType } from '../types';
import { findingService } from '../findingService';

interface CreateFindingModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateFindingModal: React.FC<CreateFindingModalProps> = ({
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [statement, setStatement] = useState('');
  const [description, setDescription] = useState('');
  const [findingType, setFindingType] = useState<FindingType>('NON_COMPLIANCE');
  const [findingCategory, setFindingCategory] = useState('OPERATIONS');
  const [sourceType, setSourceType] = useState<FindingSourceType>('AUDIT');
  const [sourceReference, setSourceReference] = useState('AUD-2026-0001');
  const [severity, setSeverity] = useState<FindingSeverity>('MAJOR');
  const [regulatoryLevel, setRegulatoryLevel] = useState('LEVEL_2');
  const [department, setDepartment] = useState('Flight Operations');
  const [location, setLocation] = useState('Istanbul Main Base (LTFM)');
  const [process, setProcess] = useState('Pre-Flight Dispatch');
  const [ownerId, setOwnerId] = useState('USR-DISPATCH-HEAD');
  const [ownerName, setOwnerName] = useState('Murat Kaya (Dispatch Manager)');
  const [verifierId, setVerifierId] = useState('AUDITOR-KEMAL');
  const [verifierName, setVerifierName] = useState('Kemal Yilmaz (Lead Auditor)');
  const [dueDate, setDueDate] = useState(
    new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0]
  );
  const [requiresRootCause, setRequiresRootCause] = useState(true);
  const [requiresCorrection, setRequiresCorrection] = useState(true);
  const [requiresVerification, setRequiresVerification] = useState(true);
  const [requiresEffectiveness, setRequiresEffectiveness] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !statement.trim()) {
      alert('Title and Finding Statement are mandatory.');
      return;
    }

    try {
      setLoading(true);
      await findingService.createFinding({
        title,
        statement,
        description,
        findingType,
        findingCategory,
        sourceType,
        sourceReference: sourceReference.trim() || undefined,
        severity,
        regulatoryLevel,
        department,
        location: location.trim() || undefined,
        process: process.trim() || undefined,
        ownerId,
        ownerName,
        verifierId: verifierId.trim() || undefined,
        verifierName: verifierName.trim() || undefined,
        dueDate,
        requiresRootCause,
        requiresCorrection,
        requiresVerification,
        requiresEffectiveness,
        createdBy: 'CURRENT_USER',
      });
      onSuccess();
    } catch (err: any) {
      alert('Failed to register finding: ' + (err?.message || err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card modal-lg">
        <div className="modal-header">
          <div className="modal-header-content">
            <h3>Register Quality Finding & Non-Conformance</h3>
            <p>
              Audit, inspection, regulatory, or operational deviation logging with closed-loop controls.
            </p>
          </div>
          <button
            onClick={onClose}
            className="modal-close-btn"
            title="Kapat"
            aria-label="Kapat"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div className="form-field">
              <label>Finding Title *</label>
              <input
                type="text"
                required
                placeholder="e.g. Flight Plan Extra Fuel Justification Omission"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div className="form-field">
              <label>Finding Statement (Specific Standard Violation) *</label>
              <textarea
                rows={2}
                required
                placeholder="e.g. In sample check of flight dispatch records (EASA ORO.AOC.135), Captain operational extra fuel reasoning was omitted."
                value={statement}
                onChange={(e) => setStatement(e.target.value)}
              />
            </div>

            <div className="form-grid-3">
              <div className="form-field">
                <label>Finding Type</label>
                <select
                  value={findingType}
                  onChange={(e) => setFindingType(e.target.value as FindingType)}
                >
                  <option value="NON_COMPLIANCE">Non-Compliance</option>
                  <option value="PROCEDURAL_DEVIATION">Procedural Deviation</option>
                  <option value="REGULATORY_GAP">Regulatory Gap</option>
                  <option value="CONTROL_DEFICIENCY">Control Deficiency</option>
                  <option value="SAFETY_DEFICIENCY">Safety Deficiency</option>
                  <option value="OBSERVATION">Observation</option>
                </select>
              </div>

              <div className="form-field">
                <label>Severity</label>
                <select
                  value={severity}
                  onChange={(e) => setSeverity(e.target.value as FindingSeverity)}
                >
                  <option value="CRITICAL">Critical (Level 1)</option>
                  <option value="MAJOR">Major (Level 2)</option>
                  <option value="MINOR">Minor (Level 3)</option>
                  <option value="INFORMATIONAL">Observation / Info</option>
                </select>
              </div>

              <div className="form-field">
                <label>Regulatory Level</label>
                <select
                  value={regulatoryLevel}
                  onChange={(e) => setRegulatoryLevel(e.target.value)}
                >
                  <option value="LEVEL_1">Level 1 (Direct Threat)</option>
                  <option value="LEVEL_2">Level 2 (Standard Non-Compliance)</option>
                  <option value="LEVEL_3">Level 3 (Administrative)</option>
                  <option value="NOT_APPLICABLE">N/A</option>
                </select>
              </div>
            </div>

            <div className="form-grid-2">
              <div className="form-field">
                <label>Source Type</label>
                <select
                  value={sourceType}
                  onChange={(e) => setSourceType(e.target.value as FindingSourceType)}
                >
                  <option value="AUDIT">Audit (Quality / Compliance)</option>
                  <option value="INSPECTION">Inspection (Physical Ramp / Facility)</option>
                  <option value="OCCURRENCE">Safety Occurrence</option>
                  <option value="INVESTIGATION">Investigation</option>
                  <option value="REGULATORY_AUTHORITY">Regulatory Authority (EASA / FAA / CAA)</option>
                  <option value="SUPPLIER">Supplier / Third-Party Audit</option>
                  <option value="SELF_REPORT">Internal Hazard / Self Report</option>
                </select>
              </div>

              <div className="form-field">
                <label>Source Reference</label>
                <input
                  type="text"
                  placeholder="e.g. AUD-2026-0001"
                  value={sourceReference}
                  onChange={(e) => setSourceReference(e.target.value)}
                />
              </div>
            </div>

            <div className="form-grid-2">
              <div className="form-field">
                <label>Target Department *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Flight Operations"
                  value={department}
                  onChange={(e) => setDepartment(e.target.value)}
                />
              </div>

              <div className="form-field">
                <label>Location / Station</label>
                <input
                  type="text"
                  placeholder="e.g. Istanbul Main Base (LTFM)"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                />
              </div>
            </div>

            <div className="form-grid-2">
              <div className="form-field">
                <label>Finding Owner / Action Assignee *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Murat Kaya (Dispatch Manager)"
                  value={ownerName}
                  onChange={(e) => setOwnerName(e.target.value)}
                />
              </div>

              <div className="form-field">
                <label>Independent Verifier (SoD)</label>
                <input
                  type="text"
                  placeholder="e.g. Kemal Yilmaz (Lead Auditor)"
                  value={verifierName}
                  onChange={(e) => setVerifierName(e.target.value)}
                />
              </div>
            </div>

            <div className="form-grid-2">
              <div className="form-field">
                <label>Target Closure Due Date *</label>
                <input
                  type="date"
                  required
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                />
              </div>

              <div className="form-field">
                <label>Process / Sub-Process</label>
                <input
                  type="text"
                  placeholder="e.g. Pre-Flight Dispatch"
                  value={process}
                  onChange={(e) => setProcess(e.target.value)}
                />
              </div>
            </div>

            {/* Closure Rules Checkboxes */}
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '14px 16px' }}>
              <span style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#334155', marginBottom: '8px' }}>
                Closure Governance & Verification Rules
              </span>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', fontSize: '13px', color: '#475569' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={requiresCorrection}
                    onChange={(e) => setRequiresCorrection(e.target.checked)}
                  />
                  <span>Immediate Correction Mandatory</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={requiresRootCause}
                    onChange={(e) => setRequiresRootCause(e.target.checked)}
                  />
                  <span>Root Cause Analysis Mandatory</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={requiresVerification}
                    onChange={(e) => setRequiresVerification(e.target.checked)}
                  />
                  <span>Independent Verification (SoD)</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={requiresEffectiveness}
                    onChange={(e) => setRequiresEffectiveness(e.target.checked)}
                  />
                  <span>Effectiveness Review Mandatory</span>
                </label>
              </div>
            </div>

            <div className="form-field">
              <label>Detailed Objective Evidence & Narrative</label>
              <textarea
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Audit observations, sampled documents, clause references, or factual notes..."
              />
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
              style={{ width: 'auto' }}
            >
              {loading ? 'Registering...' : 'Register Finding'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateFindingModal;
